import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { ToastNotification, ToastColor, toasterService } from './toasterService';
import {
  Check,
  X,
  AlertTriangle,
  XCircle,
  Info,
  LucideIcon
} from 'lucide-react';

const TOAST_DURATION = 5000; // 5 secondes

interface ToastColorConfig {
  bgColor: string;
  iconBgColor: string;
  iconColor: string;
  borderColor: string;
  progressBarColor: string;
  Icon: LucideIcon;
}

const getColorConfig = (color?: ToastColor): ToastColorConfig => {
  switch (color) {
    case GenericColors.Green:
      return {
        bgColor: 'bg-white',
        iconBgColor: 'bg-green-100',
        iconColor: 'text-green-600',
        borderColor: 'border-green-200',
        progressBarColor: 'bg-green-500',
        Icon: Check
      };
    case GenericColors.Yellow:
      return {
        bgColor: 'bg-white',
        iconBgColor: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        borderColor: 'border-yellow-200',
        progressBarColor: 'bg-yellow-500',
        Icon: AlertTriangle
      };
    case GenericColors.Red:
      return {
        bgColor: 'bg-white',
        iconBgColor: 'bg-red-100',
        iconColor: 'text-red-600',
        borderColor: 'border-red-200',
        progressBarColor: 'bg-red-500',
        Icon: XCircle
      };
    case GenericColors.Primary:
    default:
      return {
        bgColor: 'bg-white',
        iconBgColor: 'bg-primary/5',
        iconColor: 'text-primary',
        borderColor: 'border-primary',
        progressBarColor: 'bg-primary',
        Icon: Info
      };
  }
};

interface ToastItemProps {
  toast: ToastNotification;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const colorConfig = useMemo(() => getColorConfig(toast.color), [toast.color]);
  const { Icon } = colorConfig;

  useEffect(() => {
    const startTime = Date.now();
    const updateInterval = 50; // Mise à jour toutes les 50ms pour une animation fluide

    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(progressTimer);
      }
    }, updateInterval);

    const dismissTimer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, TOAST_DURATION);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(dismissTimer);
    };
  }, [toast.id, onDismiss]);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  return (
    <div
      className={`
        relative overflow-hidden
        flex flex-col rounded-lg shadow-lg border
        min-w-[320px] max-w-[420px]
        transform transition-all duration-300 ease-in-out
        ${colorConfig.bgColor} ${colorConfig.borderColor}
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex-shrink-0 p-1 rounded-full ${colorConfig.iconBgColor}`}>
          <Icon className={`w-4 h-4 ${colorConfig.iconColor}`} />
        </div>

        <div className="flex-1 min-w-0">
          {toast.title && (
            <p className="text-sm font-semibold text-gray-900 truncate">
              {toast.title}
            </p>
          )}
          {toast.subtitle && (
            <p className="text-xs text-gray-500 truncate">
              {toast.subtitle}
            </p>
          )}
          <p className="text-sm text-gray-700 mt-1">
            {toast.message}
          </p>
        </div>

        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full bg-gray-100">
        <div
          className={`h-full transition-all duration-100 ease-linear ${colorConfig.progressBarColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export const Toaster: React.FC = () => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  useEffect(() => {
    const unsubscribe = toasterService.subscribe((toast) => {
      if (toast.displayType === DisplayTypeEnum.Toaster) {
        setToasts((prev) => [...prev, toast]);
      }
    });

    return unsubscribe;
  }, []);

  const handleDismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
      ))}
    </div>,
    document.body
  );
};
